/** A day as `YYYY-MM-DD`, in the browser's own time zone. */
export type Day = string;

const DAY = /^(\d{4})-(\d{2})-(\d{2})$/;
const pad = (n: number) => String(n).padStart(2, "0");
const toDay = (date: Date): Day => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/** Local midnight at the start of `day`. */
const startOf = (day: Day) => {
  const [, y, m, d] = day.match(DAY)!.map(Number) as [number, number, number, number];
  return new Date(y, m - 1, d);
};

export const today = (): Day => toDay(new Date());

/** The day in the URL, if it's a real `YYYY-MM-DD` date, else today. */
export function parseDay(value: string | null): Day {
  if (!value || !DAY.test(value)) return today();
  return toDay(startOf(value)) === value ? value : today();
}

/** The day `count` days later (or earlier, if negative). Daylight saving changes are handled by `Date`. */
export function shiftDay(day: Day, count: number): Day {
  const date = startOf(day);
  date.setDate(date.getDate() + count);
  return toDay(date);
}

/** Local midnight to the next local midnight, as ISO strings for the API. */
export function dayRange(day: Day): { from: string; to: string } {
  return { from: startOf(day).toISOString(), to: startOf(shiftDay(day, 1)).toISOString() };
}

/** e.g. "Monday 2 November 2026", in the browser's locale. */
export const formatDay = (day: Day) =>
  startOf(day).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

/** e.g. "09:30", in the browser's locale and time zone. */
export const formatTime = (iso: string) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
